import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Dice5,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Play,
  Square,
  Trophy,
  RotateCcw,
  CheckCircle2,
  Clock,
} from 'lucide-react';
// import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useEvents,
  useCreateEvent,
  useUpdateEvent,
  useDeleteEvent,
  useStartEvent,
  useEndEvent,
  useDrawEvent,
} from '@/hooks/useEvents';
import type { Event, EventType, EventStatus } from '@/api/events.api';
import { formatCurrency, formatDate } from '@/lib/utils';
// Forms específicos por tipo de juego (clones de fedesuete). Cada uno llama
// useCreateEvent/eventsApi.create internamente; sólo le pasamos un callback de
// éxito. Las firmas difieren (onCreated vs onSaved/onError) → ver TYPE_FORMS.
import { RuletaForm } from '@/components/games/RuletaForm';
import { RaspaForm } from '@/components/games/RaspaForm';
import { SlotForm } from '@/components/games/SlotForm';
import { QuizForm } from '@/components/games/QuizForm';
import { MaletinForm } from '@/components/games/MaletinForm';
import { CofreForm } from '@/components/games/CofreForm';
import { RojoNegroForm } from '@/components/games/RojoNegroForm';
import { SorteoForm } from '@/components/games/SorteoForm';

export const Route = createFileRoute('/_authenticated/eventos')({
  component: EventosPage,
});

const TYPE_LABELS: Record<EventType, string> = {
  sorteo: 'Sorteo',
  quiz: 'Quiz',
  raspa: 'Raspadita',
  ruleta: 'Ruleta',
  rojo_negro: 'Rojo/Negro',
  slot: 'Slot',
  maletin: 'Maletín',
  cofre: 'Cofre',
};

const STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Borrador',
  active: 'Activo',
  paused: 'Pausado',
  ended: 'Finalizado',
  cancelled: 'Cancelado',
};

const STATUS_VARIANTS: Record<EventStatus, 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline'> = {
  draft: 'secondary',
  active: 'success',
  paused: 'warning',
  ended: 'default',
  cancelled: 'destructive',
};

const TYPE_VALUES: EventType[] = [
  'sorteo',
  'quiz',
  'raspa',
  'ruleta',
  'rojo_negro',
  'slot',
  'maletin',
  'cofre',
];

const formSchema = z.object({
  type: z.enum([
    'sorteo',
    'quiz',
    'raspa',
    'ruleta',
    'rojo_negro',
    'slot',
    'maletin',
    'cofre',
  ]),
  name: z.string().min(1, 'El nombre es requerido').max(160),
  description: z.string().max(2000).optional(),
  depositMin: z.string().optional(),
  prizeKind: z.string().max(40).optional(),
  prizeAmount: z.string().optional(),
  prizeDescription: z.string().max(500).optional(),
  durationMin: z.coerce.number().int().min(0).optional(),
  startsAt: z.string().optional(),
  endsAt: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Forms específicos por tipo, cada uno clon de la pantalla de fedesuete. Se
 * montan SÓLO al CREAR (el form genérico de abajo se reserva para editar).
 *
 * Las firmas no son homogéneas:
 *   - onCreated:        ruleta · raspa · slot · quiz · maletin · rojo_negro
 *   - onSaved:          sorteo
 *   - onSaved/onError:  cofre
 * `SpecificGameForm` normaliza todo a un único `onDone()` (refresca + cierra).
 */
function SpecificGameForm({
  type,
  onDone,
}: {
  type: EventType;
  onDone: () => void;
}) {
  switch (type) {
    case 'ruleta':
      return <RuletaForm onCreated={onDone} />;
    case 'raspa':
      return <RaspaForm onCreated={onDone} />;
    case 'slot':
      return <SlotForm onCreated={onDone} />;
    case 'quiz':
      return <QuizForm onCreated={onDone} />;
    case 'maletin':
      return <MaletinForm onCreated={onDone} />;
    case 'rojo_negro':
      return <RojoNegroForm onCreated={onDone} />;
    case 'sorteo':
      return <SorteoForm onSaved={onDone} />;
    case 'cofre':
      return <CofreForm onSaved={onDone} />;
    default:
      return null;
  }
}

function EventosPage() {
  const { data: events = [], isLoading } = useEvents();
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent('');
  const deleteMutation = useDeleteEvent();
  const startMutation = useStartEvent();
  const endMutation = useEndEvent();
  const drawMutation = useDrawEvent();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Event | null>(null);
  const [deleting, setDeleting] = useState<Event | null>(null);
  // Tipo elegido al CREAR un evento nuevo: decide qué form específico se monta.
  const [createType, setCreateType] = useState<EventType>('sorteo');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: 'sorteo',
      name: '',
      description: '',
      depositMin: '',
      prizeKind: '',
      prizeAmount: '',
      prizeDescription: '',
      durationMin: 0,
      startsAt: '',
      endsAt: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    const body = {
      ...values,
      depositMin: values.depositMin || undefined,
      prizeAmount: values.prizeAmount || undefined,
      startsAt: values.startsAt ? new Date(values.startsAt).toISOString() : undefined,
      endsAt: values.endsAt ? new Date(values.endsAt).toISOString() : undefined,
    };

    if (editing) {
      await updateMutation.mutateAsync(body);
    } else {
      await createMutation.mutateAsync(body);
    }
    setIsOpen(false);
    setEditing(null);
    form.reset();
  };

  const handleEdit = (evt: Event) => {
    setEditing(evt);
    form.reset({
      type: evt.type,
      name: evt.name,
      description: evt.description ?? '',
      depositMin: evt.depositMin ?? '',
      prizeKind: evt.prizeKind ?? '',
      prizeAmount: evt.prizeAmount ?? '',
      prizeDescription: evt.prizeDescription ?? '',
      durationMin: evt.durationMin ?? 0,
      startsAt: evt.startsAt ? new Date(evt.startsAt).toISOString().slice(0, 16) : '',
      endsAt: evt.endsAt ? new Date(evt.endsAt).toISOString().slice(0, 16) : '',
    });
    setIsOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const handleStart = async (id: string) => {
    await startMutation.mutateAsync(id);
  };

  const handleEnd = async (id: string) => {
    await endMutation.mutateAsync(id);
  };

  const handleDraw = async (id: string) => {
    await drawMutation.mutateAsync(id);
  };

  const columns = useMemo<ColumnDef<Event>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nombre',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.name}</span>
            {row.original.description && (
              <span className="text-xs text-muted-foreground max-w-[200px] truncate">
                {row.original.description}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Tipo',
        cell: ({ row }) => (
          <Badge variant="outline">{TYPE_LABELS[row.original.type]}</Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status]}>
            {STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        accessorKey: 'prizeAmount',
        header: 'Premio',
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {row.original.prizeAmount ? formatCurrency(row.original.prizeAmount) : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'startsAt',
        header: 'Inicio',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.startsAt ? formatDate(row.original.startsAt) : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'endsAt',
        header: 'Fin',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.endsAt ? formatDate(row.original.endsAt) : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const evt = row.original;
          return (
            <div className="flex items-center gap-1">
              {evt.status === 'draft' && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-green-600"
                  onClick={() => handleStart(evt.id)}
                  disabled={startMutation.isPending}
                  title="Iniciar"
                >
                  <Play className="h-4 w-4" />
                </Button>
              )}
              {evt.status === 'active' && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-amber-600"
                    onClick={() => handleEnd(evt.id)}
                    disabled={endMutation.isPending}
                    title="Finalizar"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-purple-600"
                    onClick={() => handleDraw(evt.id)}
                    disabled={drawMutation.isPending}
                    title="Sortear"
                  >
                    <Trophy className="h-4 w-4" />
                  </Button>
                </>
              )}
              {evt.status === 'ended' && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-purple-600"
                    onClick={() => handleDraw(evt.id)}
                    disabled={drawMutation.isPending}
                    title="Sortear ganador"
                  >
                    <Trophy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-green-600"
                    onClick={() => handleStart(evt.id)}
                    disabled={startMutation.isPending}
                    title="Reactivar (ronda nueva)"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </>
              )}
              {evt.status === 'paused' && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-green-600"
                  onClick={() => handleStart(evt.id)}
                  disabled={startMutation.isPending}
                  title="Reactivar (ronda nueva)"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => handleEdit(evt)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="text-red-500"
                onClick={() => setDeleting(evt)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const activeCount = events.filter((e) => e.status === 'active').length;
  const endedCount = events.filter((e) => e.status === 'ended').length;

  return (
    <>
      <TopBanner
        title="Eventos"
        subtitle="Sorteos, quiz, ruleta, raspaditas"
      />
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-4">
        <div className="text-sm text-muted-foreground">
          {events.length} evento(s) en total
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setCreateType('sorteo');
            form.reset({
              type: 'sorteo',
              name: '',
              description: '',
              depositMin: '',
              prizeKind: '',
              prizeAmount: '',
              prizeDescription: '',
              durationMin: 0,
              startsAt: '',
              endsAt: '',
            });
            setIsOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuevo evento
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-primary/10 p-2.5">
                <Dice5 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{events.length}</div>
                <div className="text-xs text-muted-foreground">Total eventos</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <Play className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{activeCount}</div>
                <div className="text-xs text-muted-foreground">Activos</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-blue-100 p-2.5">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{endedCount}</div>
                <div className="text-xs text-muted-foreground">Finalizados</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {events.filter((e) => e.status === 'draft').length}
                </div>
                <div className="text-xs text-muted-foreground">Borradores</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DataTable
          columns={columns}
          data={events}
          isLoading={isLoading}
          emptyMessage="No hay eventos configurados"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar evento' : 'Nuevo evento'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Editá los datos del evento gamificado.'
                : 'Elegí el tipo de juego y configurá sus parámetros.'}
            </DialogDescription>
          </DialogHeader>

          {/* CREAR: selector de tipo + form específico por juego. */}
          {!editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <span className="text-sm font-medium">Tipo de juego</span>
                <Select
                  value={createType}
                  onValueChange={(v) => setCreateType(v as EventType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_VALUES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <SpecificGameForm
                key={createType}
                type={createType}
                onDone={() => {
                  setIsOpen(false);
                  form.reset();
                }}
              />
            </div>
          )}

          {/* EDITAR: form genérico (campos planos del Event). */}
          {editing && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TYPE_VALUES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Sorteo de verano" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descripción</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Descripción del evento..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="prizeKind"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de premio</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Fichas, bono..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="prizeAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monto del premio</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="1000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="depositMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Depósito mínimo</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="durationMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duración (min)</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha inicio</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha fin</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar evento?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará <strong>{deleting.name}</strong> ({TYPE_LABELS[deleting.type]}).
                  Esta acción no se puede deshacer.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
